import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Building2, Plus, Pencil, Trash2, Hospital, Users as UsersIcon, Ban, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { confirm } from "@/lib/confirm";
import { useAuth } from "@/contexts/AuthContext";

export default function EmpresasPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", short_name: "", copy_catalog: true, link_all_hospitals: true });
  const [editing, setEditing] = useState(null);
  const [hospDialog, setHospDialog] = useState(null); // { empresa, hospitals }
  const [accessDialog, setAccessDialog] = useState(null); // { empresa, users }
  const [saving, setSaving] = useState(false);

  async function load() {
    try { const r = await api.get("/empresas"); setItems(r.data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }
  useEffect(() => { load(); }, []);

  if (user && !user.is_super_admin) {
    return (
      <div className="max-w-lg mx-auto mt-16 text-center space-y-2" data-testid="empresas-restricted">
        <Ban className="h-10 w-10 text-slate-300 mx-auto" />
        <h1 className="font-display text-2xl font-bold text-slate-900">Acesso restrito</h1>
        <p className="text-sm text-slate-500">A administração de empresas é exclusiva do administrador principal.</p>
      </div>
    );
  }

  async function create() {
    if (!form.name.trim()) { toast.error("Informe o nome da empresa"); return; }
    setSaving(true);
    try {
      const r = await api.post("/empresas", form);
      toast.success(`Empresa "${r.data.name}" criada${r.data.procedures_copied ? ` — ${r.data.procedures_copied} procedimento(s) copiado(s)` : ""}`);
      setCreating(false);
      setForm({ name: "", short_name: "", copy_catalog: true, link_all_hospitals: true });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  }

  async function saveEdit() {
    setSaving(true);
    try {
      await api.patch(`/empresas/${editing.id}`, {
        name: editing.name, short_name: editing.short_name || null, active: editing.active,
      });
      toast.success("Empresa atualizada");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  }

  async function remove(emp) {
    if (!(await confirm({ description: `Excluir a empresa ${emp.name}? Só é possível se ela não tiver mapas/casos.`, variant: "destructive" }))) return;
    try { await api.delete(`/empresas/${emp.id}`); toast.success("Excluída"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  async function openHospitals(emp) {
    try {
      const r = await api.get(`/empresas/${emp.id}/hospitals`);
      setHospDialog({ empresa: emp, hospitals: r.data });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  async function saveHospitals() {
    setSaving(true);
    try {
      const ids = hospDialog.hospitals.filter((h) => h.linked).map((h) => h.id);
      await api.put(`/empresas/${hospDialog.empresa.id}/hospitals`, { hospital_ids: ids });
      toast.success("Hospitais vinculados atualizados");
      setHospDialog(null);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  }

  async function openAccess(emp) {
    try {
      const r = await api.get("/admin/users");
      setAccessDialog({ empresa: emp, users: r.data });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  function toggleAccess(uid) {
    setAccessDialog((d) => ({
      ...d,
      users: d.users.map((u) => {
        if (u.id !== uid) return u;
        const set = new Set(u.empresa_ids || []);
        if (set.has(d.empresa.id)) set.delete(d.empresa.id); else set.add(d.empresa.id);
        return { ...u, empresa_ids: Array.from(set), _changed: true };
      }),
    }));
  }

  async function saveAccess() {
    setSaving(true);
    try {
      const changed = accessDialog.users.filter((u) => u._changed);
      for (const u of changed) {
        await api.patch(`/admin/users/${u.id}`, { empresa_ids: u.empresa_ids || [] });
      }
      toast.success(`Acessos atualizados (${changed.length} usuário(s))`);
      setAccessDialog(null);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  }

  return (
    <div className="space-y-5" data-testid="empresas-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Empresas</h1>
          <p className="text-sm text-slate-500 mt-1">
            Gestão multiempresa: cada empresa tem seus próprios mapas, casos, profissionais e regras financeiras.
            Hospitais são compartilhados — vincule quais cada empresa utiliza.
          </p>
        </div>
        <Button className="bg-blue-700 hover:bg-blue-800" onClick={() => setCreating(true)} data-testid="empresas-btn-new">
          <Plus className="h-4 w-4 mr-2" /> Nova empresa
        </Button>
      </div>

      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-600">
              <tr>
                <th className="text-left px-4 py-3">Empresa</th>
                <th className="text-left px-4 py-3">Casos</th>
                <th className="text-left px-4 py-3">Mapas</th>
                <th className="text-left px-4 py-3">Pacientes</th>
                <th className="text-left px-4 py-3">Cirurgiões</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="w-44"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((e) => (
                <tr key={e.id} className="border-b border-slate-100" data-testid={`empresa-row-${e.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-blue-700" />
                      <span className="font-medium">{e.name}</span>
                      {e.short_name && e.short_name !== e.name && (
                        <span className="text-xs text-slate-400">({e.short_name})</span>
                      )}
                      {e.is_default && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 border border-blue-200 text-blue-700 uppercase tracking-wider font-semibold">Padrão</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">{e.counts?.cases ?? "—"}</td>
                  <td className="px-4 py-3">{e.counts?.filtered_maps ?? "—"}</td>
                  <td className="px-4 py-3">{e.counts?.patients ?? "—"}</td>
                  <td className="px-4 py-3">{e.counts?.surgeons ?? "—"}</td>
                  <td className="px-4 py-3">
                    {e.active !== false ? (
                      <span className="inline-flex items-center gap-1 text-emerald-700 text-xs font-medium"><CheckCircle2 className="h-3.5 w-3.5" /> Ativa</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-slate-400 text-xs font-medium"><Ban className="h-3.5 w-3.5" /> Inativa</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <Button size="icon" variant="ghost" className="h-8 w-8" title="Hospitais vinculados"
                      onClick={() => openHospitals(e)} data-testid={`btn-hospitals-${e.id}`}>
                      <Hospital className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8" title="Acessos de usuários"
                      onClick={() => openAccess(e)} data-testid={`btn-access-${e.id}`}>
                      <UsersIcon className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8" title="Editar"
                      onClick={() => setEditing({ ...e })} data-testid={`btn-edit-empresa-${e.id}`}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    {!e.is_default && (
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" title="Excluir"
                        onClick={() => remove(e)} data-testid={`btn-delete-empresa-${e.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Nova empresa */}
      <Dialog open={creating} onOpenChange={(o) => { if (!o) setCreating(false); }}>
        <DialogContent data-testid="empresa-create-dialog">
          <DialogHeader>
            <DialogTitle>Nova empresa</DialogTitle>
            <DialogDescription>
              A nova empresa começa com as mesmas regras financeiras da BVSM (editáveis depois em Configurações).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label>Nome *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="empresa-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Sigla / nome curto</Label>
              <Input value={form.short_name} onChange={(e) => setForm({ ...form, short_name: e.target.value })} data-testid="empresa-shortname-input" />
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox checked={form.copy_catalog} onCheckedChange={(v) => setForm({ ...form, copy_catalog: !!v })} data-testid="empresa-copy-catalog" />
              Copiar catálogo de procedimentos da BVSM
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox checked={form.link_all_hospitals} onCheckedChange={(v) => setForm({ ...form, link_all_hospitals: !!v })} data-testid="empresa-link-hospitals" />
              Vincular todos os hospitais existentes
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreating(false)}>Cancelar</Button>
            <Button onClick={create} disabled={saving} className="bg-blue-700 hover:bg-blue-800" data-testid="empresa-create-save">
              {saving ? "Criando..." : "Criar empresa"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Editar empresa */}
      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent data-testid="empresa-edit-dialog">
          <DialogHeader>
            <DialogTitle>Editar empresa</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3 py-2">
              <div className="space-y-1.5">
                <Label>Nome</Label>
                <Input value={editing.name || ""} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="empresa-edit-name" />
              </div>
              <div className="space-y-1.5">
                <Label>Sigla / nome curto</Label>
                <Input value={editing.short_name || ""} onChange={(e) => setEditing({ ...editing, short_name: e.target.value })} data-testid="empresa-edit-shortname" />
              </div>
              {!editing.is_default && (
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox checked={editing.active !== false} onCheckedChange={(v) => setEditing({ ...editing, active: !!v })} data-testid="empresa-edit-active" />
                  Ativa
                </label>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancelar</Button>
            <Button onClick={saveEdit} disabled={saving} className="bg-blue-700 hover:bg-blue-800" data-testid="empresa-edit-save">
              {saving ? "Salvando..." : "Salvar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Hospitais vinculados */}
      <Dialog open={!!hospDialog} onOpenChange={(o) => { if (!o) setHospDialog(null); }}>
        <DialogContent data-testid="empresa-hospitals-dialog">
          <DialogHeader>
            <DialogTitle>Hospitais — {hospDialog?.empresa?.name}</DialogTitle>
            <DialogDescription>
              Marque os hospitais em que esta empresa opera. O cadastro de hospitais é compartilhado entre empresas.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2 max-h-72 overflow-y-auto">
            {(hospDialog?.hospitals || []).map((h) => (
              <label key={h.id} className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox
                  checked={h.linked}
                  onCheckedChange={() => setHospDialog((d) => ({
                    ...d,
                    hospitals: d.hospitals.map((x) => x.id === h.id ? { ...x, linked: !x.linked } : x),
                  }))}
                  data-testid={`hospital-link-${h.id}`}
                />
                {h.name}
              </label>
            ))}
            {(hospDialog?.hospitals || []).length === 0 && (
              <p className="text-sm text-slate-400">Nenhum hospital cadastrado.</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHospDialog(null)}>Cancelar</Button>
            <Button onClick={saveHospitals} disabled={saving} className="bg-blue-700 hover:bg-blue-800" data-testid="empresa-hospitals-save">
              {saving ? "Salvando..." : "Salvar vínculos"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Acessos de usuários */}
      <Dialog open={!!accessDialog} onOpenChange={(o) => { if (!o) setAccessDialog(null); }}>
        <DialogContent data-testid="empresa-access-dialog">
          <DialogHeader>
            <DialogTitle>Acessos — {accessDialog?.empresa?.name}</DialogTitle>
            <DialogDescription>
              Marque quem pode acessar esta empresa. Administradores acessam todas (selecionando uma por vez).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2 max-h-72 overflow-y-auto">
            {(accessDialog?.users || []).map((u) => (
              <div key={u.id} className="flex items-center gap-2 text-sm">
                {u.role === "admin" ? (
                  <>
                    <Checkbox checked disabled />
                    <span>{u.name} <span className="text-xs text-slate-400">({u.email}) — admin, acesso total</span></span>
                  </>
                ) : (
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={(u.empresa_ids || []).includes(accessDialog.empresa.id)}
                      onCheckedChange={() => toggleAccess(u.id)}
                      data-testid={`access-user-${u.id}`}
                    />
                    <span>{u.name} <span className="text-xs text-slate-400">({u.email})</span></span>
                  </label>
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAccessDialog(null)}>Cancelar</Button>
            <Button onClick={saveAccess} disabled={saving} className="bg-blue-700 hover:bg-blue-800" data-testid="empresa-access-save">
              {saving ? "Salvando..." : "Salvar acessos"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
